import { useMemo, useState } from 'react';
import {
  Bell,
  Bot,
  ChevronDown,
  ExternalLink,
  FileText,
  Loader2,
  Server,
  Square,
  Terminal,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { timeAgo } from '../../lib/format';
import { SectionLabel } from '../layout/Typography';
import { StatusDot } from '../ui/status-dot';
import {
  AgentThreadRow,
  orderByAttention,
  threadTone,
  type AgentThreadListEntry,
} from './AgentThreadList';

export interface AgentActivityBackgroundTask {
  id: string;
  kind: string;
  label: string;
}

/** A background process the agent registered on its sandbox. */
export interface AgentActivityDaemon {
  name: string;
  port?: number | null;
  /** Tunnel URL when the daemon is exposed; renders an open-in-browser affordance. */
  url?: string | null;
  info?: string | null;
  /** Epoch ms; renders a "started Xm ago" hint when provided. */
  startedAt?: number | null;
  /** Live process state; defaults to the optimistic "it registered once" running. */
  state?: 'running' | 'stopped' | 'unknown';
  /** The daemon matched a live on-box service, so a log viewer can target it. */
  hasLogs?: boolean;
}

/**
 * A listener the agent armed for itself (a timer, a signal on a ref it holds,
 * or a watch on an event stream), as the dock shows it. The host maps the
 * server row to this: the label is the ref handle or the timer's delay, the
 * secondary line the lease and reason.
 */
export interface AgentActivityListener {
  id: string;
  /** "PR #42 · reviews", "in 1h 42m", "every PR in acme/reflex". */
  label: string;
  /** "6d left · a review landed on my PR" */
  secondary?: string | null;
  state: 'armed' | 'pending' | 'fired' | 'expired' | 'cancelled' | 'denied';
  /** The fire interrupts a running turn. */
  interrupts?: boolean;
  /** Times fired so far, shown when recurring. */
  fireCount?: number;
  /** The requesting user's avatar URL, so attribution is visible before it fires. */
  requesterAvatarUrl?: string | null;
  canRenew?: boolean;
  canCancel?: boolean;
}

const LISTENER_DOT_TONE = {
  armed: 'success',
  pending: 'warning',
  fired: 'neutral',
  expired: 'neutral',
  cancelled: 'neutral',
  denied: 'error',
} as const;

const LISTENER_STATE_LABEL: Record<AgentActivityListener['state'], string> = {
  armed: 'armed',
  pending: 'waiting for approval',
  fired: 'fired',
  expired: 'expired',
  cancelled: 'cancelled',
  denied: 'denied',
};

const DAEMON_DOT_TONE = {
  running: 'success',
  stopped: 'error',
  unknown: 'neutral',
} as const;

function count(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? '' : 's'}`;
}

/**
 * The collapsed line's contents, derived once so the summary and its status
 * dot always agree. Exported for unit tests.
 */
export function activitySummary(
  subagents: AgentThreadListEntry[],
  daemons: AgentActivityDaemon[],
  backgroundTasks: AgentActivityBackgroundTask[] = [],
  listeners: AgentActivityListener[] = [],
): {
  tone: 'success' | 'warning' | 'error' | 'muted';
  pulse: boolean;
  label: string;
  /** Headline of the thread that most needs attention, if any thread is live. */
  liveHint: string | null;
} {
  // The line stays terse: only what is live counts. "1 agent" means one
  // running now (running is assumed); finished launches are not tallied
  // unless the label would otherwise be empty.
  const working = subagents.filter((entry) => entry.turnState === 'working').length;
  const parts: string[] = [];
  if (working > 0) parts.push(count(working, 'agent'));
  if (backgroundTasks.length > 0) parts.push(count(backgroundTasks.length, 'background task'));
  if (daemons.length > 0) parts.push(count(daemons.length, 'daemon'));
  // Live listeners only: armed or waiting on a person. Spent ones stay in
  // the panel for the record but do not count on the line.
  const liveListeners = listeners.filter((l) => l.state === 'armed' || l.state === 'pending');
  if (liveListeners.length > 0) parts.push(count(liveListeners.length, 'listener'));
  if (parts.length === 0 && subagents.length > 0) {
    parts.push(`${count(subagents.length, 'agent')} done`);
  }

  const top = orderByAttention(subagents)[0];
  const pending = listeners.find((l) => l.state === 'pending');
  const fromThread = top
    ? threadTone(top)
    : {
        tone: backgroundTasks.length > 0 ? ('success' as const) : ('muted' as const),
        pulse: false,
      };
  // A listener waiting on the user outranks everything but a blocked
  // sub-agent: it is the one thing on the line only a person can move.
  const { tone, pulse } =
    pending && top?.mark !== 'blocked' ? { tone: 'warning' as const, pulse: true } : fromThread;
  const liveHint =
    top && (top.turnState === 'working' || top.mark === 'blocked')
      ? top.headline
      : pending
        ? `Approve? ${pending.label}`
        : null;
  return { tone, pulse: pulse || backgroundTasks.length > 0, label: parts.join(' · '), liveHint };
}

function DaemonRow({
  daemon,
  withTopBorder,
  onOpenLogs,
}: {
  daemon: AgentActivityDaemon;
  withTopBorder: boolean;
  onOpenLogs?: () => void;
}) {
  const secondary = [
    daemon.state === 'stopped' ? 'not running' : '',
    daemon.info,
    daemon.startedAt != null ? `started ${timeAgo(daemon.startedAt)}` : '',
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <div
      data-testid="agent-activity-daemon-row"
      className={cn(
        'flex w-full items-center gap-2 px-2.5 py-2',
        withTopBorder && 'border-t border-border/40',
      )}
    >
      <StatusDot tone={DAEMON_DOT_TONE[daemon.state ?? 'running']} size="sm" />
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-1.5">
          <span className="truncate text-xs font-medium">{daemon.name}</span>
          {daemon.port != null && (
            <span className="shrink-0 font-mono text-2xs text-muted-foreground">
              :{daemon.port}
            </span>
          )}
        </span>
        {secondary && (
          <span
            className={cn(
              'block truncate text-2xs text-muted-foreground',
              daemon.state === 'stopped' && 'text-status-error',
            )}
          >
            {secondary}
          </span>
        )}
      </span>
      {daemon.hasLogs && onOpenLogs && (
        <button
          type="button"
          onClick={onOpenLogs}
          aria-label={`View ${daemon.name} logs`}
          className="inline-flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <FileText className="icon-xs" aria-hidden="true" />
        </button>
      )}
      {daemon.url && (
        <a
          href={daemon.url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Open ${daemon.name} in a new tab`}
          className="inline-flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <ExternalLink className="icon-xs" aria-hidden="true" />
        </a>
      )}
    </div>
  );
}

function ListenerRow({
  listener,
  withTopBorder,
  onDecide,
  onCancel,
  onRenew,
}: {
  listener: AgentActivityListener;
  withTopBorder: boolean;
  onDecide?: (answer: 'allow_agent' | 'deny') => void;
  onCancel?: () => void;
  onRenew?: () => void;
}) {
  const secondary = [
    LISTENER_STATE_LABEL[listener.state],
    listener.interrupts ? 'interrupts' : '',
    listener.fireCount ? `fired ${listener.fireCount}×` : '',
    listener.secondary,
  ]
    .filter(Boolean)
    .join(' · ');
  const action =
    'inline-flex h-6 shrink-0 items-center rounded-md px-1.5 text-2xs font-medium transition-colors hover:bg-accent';
  return (
    <div
      data-testid="agent-activity-listener-row"
      className={cn(
        'flex w-full items-center gap-2 px-2.5 py-2',
        withTopBorder && 'border-t border-border/40',
      )}
    >
      <StatusDot
        tone={LISTENER_DOT_TONE[listener.state]}
        pulse={listener.state === 'pending'}
        size="sm"
      />
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-1.5">
          <span className="truncate text-xs font-medium">{listener.label}</span>
        </span>
        {secondary && (
          <span
            className={cn(
              'block truncate text-2xs text-muted-foreground',
              listener.state === 'pending' && 'text-status-warning',
            )}
          >
            {secondary}
          </span>
        )}
      </span>
      {listener.requesterAvatarUrl && (
        <img src={listener.requesterAvatarUrl} alt="" className="size-4 shrink-0 rounded-full" />
      )}
      {listener.state === 'pending' && onDecide && (
        <>
          <button
            type="button"
            onClick={() => onDecide('allow_agent')}
            aria-label={`Allow ${listener.label}`}
            className={cn(action, 'text-status-success')}
          >
            Allow
          </button>
          <button
            type="button"
            onClick={() => onDecide('deny')}
            aria-label={`Deny ${listener.label}`}
            className={cn(action, 'text-muted-foreground')}
          >
            Deny
          </button>
        </>
      )}
      {listener.state === 'armed' && listener.canRenew && onRenew && (
        <button
          type="button"
          onClick={onRenew}
          aria-label={`Renew ${listener.label}`}
          className={cn(action, 'text-muted-foreground')}
        >
          Renew
        </button>
      )}
      {(listener.state === 'armed' || listener.state === 'pending') &&
        listener.canCancel !== false &&
        onCancel && (
          <button
            type="button"
            onClick={onCancel}
            aria-label={`Cancel ${listener.label}`}
            className={cn(action, 'text-muted-foreground')}
          >
            Cancel
          </button>
        )}
    </div>
  );
}

/**
 * Section divider inside the expanded panel. The tinted band is what makes
 * a header read as a group boundary rather than another row: rows sit on
 * the card surface, headers sit on a muted strip with their own borders.
 */
function ActivitySectionLabel({
  icon: Icon,
  first,
  children,
}: {
  icon: typeof Bot;
  first?: boolean;
  children: string;
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-1.5 border-b border-border/40 bg-muted/40 px-2.5 py-1.5 text-muted-foreground/70',
        !first && 'border-t',
      )}
    >
      <Icon className="icon-xs" aria-hidden="true" />
      <SectionLabel as="span">{children}</SectionLabel>
    </div>
  );
}

interface AgentActivityBarProps {
  /** Sub-agent launches inside this thread, newest first. */
  subagents: AgentThreadListEntry[];
  daemons: AgentActivityDaemon[];
  backgroundTasks?: AgentActivityBackgroundTask[];
  onCancelBackgroundTask?: (id: string) => void;
  cancellingTaskIds?: ReadonlySet<string>;
  /** Listeners the agent armed; pending ones carry inline Allow / Deny. */
  listeners?: AgentActivityListener[];
  onDecideListener?: (id: string, answer: 'allow_agent' | 'deny') => void;
  onCancelListener?: (id: string) => void;
  onRenewListener?: (id: string) => void;
  /** When provided, sub-agent rows become buttons that jump to the launch. */
  onOpenSubagent?: (id: string) => void;
  /** Open the log viewer for the daemon at this index (rendered only when the daemon `hasLogs`). */
  onOpenDaemonLogs?: (index: number) => void;
  /** Fires on every expand/collapse; lets the host probe live state only while open. */
  onExpandedChange?: (expanded: boolean) => void;
  defaultExpanded?: boolean;
  className?: string;
}

/**
 * A one-line activity rail meant to dock directly above the composer: the
 * sub-agents this thread has fanned out and the daemons it is hosting,
 * folded into a single glanceable summary line. Expanding lifts the full
 * lists — sub-agents in the shared thread-row form, daemons with their port
 * and tunnel link — without the transcript having to scroll to them.
 */
export function AgentActivityBar({
  subagents,
  daemons,
  backgroundTasks = [],
  onCancelBackgroundTask,
  cancellingTaskIds,
  listeners = [],
  onDecideListener,
  onCancelListener,
  onRenewListener,
  onOpenSubagent,
  onOpenDaemonLogs,
  onExpandedChange,
  defaultExpanded = false,
  className,
}: AgentActivityBarProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const summary = useMemo(
    () => activitySummary(subagents, daemons, backgroundTasks, listeners),
    [subagents, daemons, backgroundTasks, listeners],
  );
  const orderedSubagents = useMemo(() => orderByAttention(subagents), [subagents]);
  // Pending first (they need a person), then armed, then the record.
  const orderedListeners = useMemo(() => {
    const rank: Record<AgentActivityListener['state'], number> = {
      pending: 0,
      armed: 1,
      fired: 2,
      expired: 3,
      denied: 4,
      cancelled: 5,
    };
    return [...listeners].sort((a, b) => rank[a.state] - rank[b.state]);
  }, [listeners]);

  if (
    subagents.length === 0 &&
    daemons.length === 0 &&
    backgroundTasks.length === 0 &&
    listeners.length === 0
  )
    return null;

  return (
    <div
      data-testid="agent-activity-bar"
      className={cn(
        'overflow-hidden rounded-xl border border-border/60 bg-card/60 shadow-xs',
        className,
      )}
    >
      {expanded && (
        <div className="max-h-72 overflow-y-auto border-b border-border/40">
          {orderedSubagents.length > 0 && (
            <>
              <ActivitySectionLabel icon={Bot} first>
                Sub-agents
              </ActivitySectionLabel>
              {orderedSubagents.map((entry, index) => (
                <AgentThreadRow
                  key={entry.id}
                  entry={entry}
                  onOpen={onOpenSubagent}
                  withTopBorder={index > 0}
                />
              ))}
            </>
          )}
          {backgroundTasks.length > 0 && (
            <>
              <ActivitySectionLabel icon={Terminal} first={orderedSubagents.length === 0}>
                Background tasks
              </ActivitySectionLabel>
              {backgroundTasks.map((task) => (
                <div
                  key={task.id}
                  data-testid={`background-task-${task.id}`}
                  className="flex items-center gap-2 px-2.5 py-2"
                >
                  <StatusDot tone="success" pulse size="sm" />
                  <span className="min-w-0 flex-1 truncate text-xs" title={task.label}>
                    {task.label}
                  </span>
                  {onCancelBackgroundTask && (task.kind === 'command' || task.kind === 'agent') && (
                    <button
                      type="button"
                      aria-label={`Cancel ${task.label}`}
                      disabled={cancellingTaskIds?.has(task.id)}
                      onClick={() => onCancelBackgroundTask(task.id)}
                      className="inline-flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50"
                    >
                      {cancellingTaskIds?.has(task.id) ? (
                        <Loader2 className="icon-xs animate-spin" aria-hidden="true" />
                      ) : (
                        <Square className="icon-xs" aria-hidden="true" />
                      )}
                    </button>
                  )}
                </div>
              ))}
            </>
          )}
          {daemons.length > 0 && (
            <>
              <ActivitySectionLabel
                icon={Server}
                first={orderedSubagents.length === 0 && backgroundTasks.length === 0}
              >
                Daemons
              </ActivitySectionLabel>
              {daemons.map((daemon, index) => (
                // Index key: name+port is not unique (two port-less daemons can
                // share a name) and the list is append-only, never reordered.
                <DaemonRow
                  key={index}
                  daemon={daemon}
                  withTopBorder={index > 0}
                  onOpenLogs={onOpenDaemonLogs ? () => onOpenDaemonLogs(index) : undefined}
                />
              ))}
            </>
          )}
          {orderedListeners.length > 0 && (
            <>
              <ActivitySectionLabel
                icon={Bell}
                first={orderedSubagents.length === 0 && daemons.length === 0}
              >
                Listeners
              </ActivitySectionLabel>
              {orderedListeners.map((listener, index) => (
                <ListenerRow
                  key={listener.id}
                  listener={listener}
                  withTopBorder={index > 0}
                  onDecide={
                    onDecideListener ? (answer) => onDecideListener(listener.id, answer) : undefined
                  }
                  onCancel={onCancelListener ? () => onCancelListener(listener.id) : undefined}
                  onRenew={onRenewListener ? () => onRenewListener(listener.id) : undefined}
                />
              ))}
            </>
          )}
        </div>
      )}

      <button
        type="button"
        data-testid="agent-activity-toggle"
        aria-expanded={expanded}
        onClick={() => {
          // Not inside the updater: strict mode double-invokes updaters.
          setExpanded(!expanded);
          onExpandedChange?.(!expanded);
        }}
        className="group flex w-full items-center gap-2 px-2.5 py-1.5 text-left transition-colors hover:bg-accent/50"
      >
        <StatusDot tone={summary.tone} pulse={summary.pulse} size="sm" />
        <span className="shrink-0 text-xs font-medium">{summary.label}</span>
        {summary.liveHint && !expanded && (
          <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
            {summary.liveHint}
          </span>
        )}
        <ChevronDown
          className={cn(
            'ml-auto icon-xs shrink-0 text-muted-foreground/50 transition-transform group-hover:text-muted-foreground',
            !expanded && 'rotate-180',
          )}
        />
      </button>
    </div>
  );
}
